import { useReactFlow } from "@xyflow/react";
import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import {
  Account,
  BuiltInApp,
  Session,
  ZSL,
  accountAtom,
  canvasAtom,
  canvasFolderIdAtom,
  canvasIdAtom,
  chatbotsAppAtom,
  edgesAtom,
  interfacesAppAtom,
  isEmbedAtom,
  isPublicAtom,
  nodesAtom,
  sessionAtom,
  tablesAppAtom,
  zslAtom,
  Toggles,
  togglesAtom,
  isViewOnlyDowntimeOverrideAtom,
  RawCanvas,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
  cursorAtom,
  agentsAppAtom,
  reactFlowInstanceAtom,
  CursorStates,
  isAgentsEnabledAtom,
} from "@zapier/canvas-state";
import { aiPromptAtom } from "@zapier/streaming";

import { convertCanvasSchemaForReactFlow } from "../../utils/convertCanvasSchemaForReactFlow";

export function CanvasHydrator({
  canvas: rawCanvas,
  children,
  session,
  account,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  agentsApp,
  toggles,
  isAgentsEnabled = false,
  isPublic = false,
  isEmbed = false,
  isAiPage = false,
  isTemplatePage = false,
  prompt = "",
  noShowModal: _noShowModal,
}: {
  canvas: RawCanvas;
  children: React.ReactNode;
  session?: Session;
  account?: Account;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  agentsApp: BuiltInApp;
  toggles: Toggles;
  /**
   * Account-level entitlement for Agents.  If omitted, we assume
   * agents are disabled to prevent accidental exposure.
   */
  isAgentsEnabled: boolean;
  isPublic?: boolean;
  isEmbed?: boolean;
  isAiPage?: boolean;
  isTemplatePage?: boolean;
  prompt?: string;
  noShowModal?: string | null;
}) {
  const isViewer = rawCanvas.access_level === "can_view"; // This will be true when is_public is true (so true for public links)

  const isViewOnlyMode =
    isAiPage ||
    isViewer ||
    isPublic ||
    isTemplatePage ||
    isEmbed ||
    toggles.view_only_downtime;
  void _noShowModal;

  const canvas = convertCanvasSchemaForReactFlow({
    isViewOnlyMode,
    rawCanvas,
  });

  const reactFlowInstance = useReactFlow();

  useHydrateAtoms([
    [isTemplatePageAtom, isTemplatePage],
    [zslAtom, template],
    [canvasAtom, canvas],
    [nodesAtom, canvas.nodes],
    [edgesAtom, canvas.edges],
    [accountAtom, account || null],
    [sessionAtom, session || null],
    [tablesAppAtom, tablesApp],
    [interfacesAppAtom, interfacesApp],
    [chatbotsAppAtom, chatbotsApp],
    [agentsAppAtom, agentsApp],
    [togglesAtom, toggles],
    [isPublicAtom, isPublic],
    [isEmbedAtom, isEmbed],
    [aiPromptAtom, prompt],
    [isViewOnlyDowntimeOverrideAtom, toggles.view_only_downtime],
    [isViewOnlyModeAtom, isViewOnlyMode],
    [cursorAtom, (isViewOnlyMode ? "pan" : "select") as CursorStates],
    [isAgentsEnabledAtom, isAgentsEnabled],
    // Store the full ReactFlow instance so atoms/hooks can access functions
    [reactFlowInstanceAtom, reactFlowInstance],
  ]);

  const fetchCanvasFolder = useSetAtom(canvasFolderIdAtom);
  const canvasId = useAtomValue(canvasIdAtom);
  useEffect(() => {
    if (!isViewOnlyMode && canvasId) {
      fetchCanvasFolder();
    }
  }, [fetchCanvasFolder, canvasId, isViewOnlyMode]);

  return children;
}
